<script>
  import { router } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import Card from '../../components/card.svelte'
  import StatGrid from '../../components/stat_grid.svelte'
  import StatTile from '../../components/stat_tile.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import FilterDate from '../../components/filter_date.svelte'
  import LineChart from '../../components/charts/line_chart.svelte'
  import AgeBadge from '../../components/badges/age_badge.svelte'

  let { metrics = null, queue = null, filters = {} } = $props()

  // The inputs show the range the server applied (defaults filled in, a reversed range swapped).
  const startDate = $derived(filters.start_date || '')
  const endDate = $derived(filters.end_date || '')

  // Like the old CMS, a picked date applies straight away.
  const applyFilters = (changes) => {
    const params = {}
    const range = { start_date: startDate, end_date: endDate, ...changes }
    if (range.start_date) params.start_date = range.start_date
    if (range.end_date) params.end_date = range.end_date
    router.get('/social-dashboard', params, { preserveState: true, preserveScroll: true })
  }

  const engagement = $derived(metrics?.engagement ?? null)
  const growth = $derived(metrics?.growth ?? null)
  const moderation = $derived(metrics?.moderation ?? null)
  const timeSeries = $derived(
    metrics?.time_series ?? { labels: [], moments_created: [], hypes_given: [], friend_requests_sent: [] }
  )

  const formatRate = (rate) => `${((rate ?? 0) * 100).toFixed(1)}%`
  // Mean hours from report creation to review; null when nothing was reviewed in the range.
  const formatHours = (hours) => (hours == null ? '—' : `${Number(hours).toFixed(1)}h`)

  const trendDatasets = $derived([
    { label: 'Moments Created', data: timeSeries.moments_created, color: '#7000ff' },
    { label: 'Hypes Given', data: timeSeries.hypes_given, color: '#ff8c00' },
    { label: 'Friend Requests', data: timeSeries.friend_requests_sent, color: '#00b894' }
  ])
</script>

<Layout>
  <PageHeader title="Social Dashboard" subtitle="Engagement, growth, and moderation health" />

  <FilterBar>
    {#snippet start()}
      <!-- Remount on every response: a cleared input whose applied date didn't change would stay blank. -->
      {#key filters}
        <FilterDate label="Start Date" value={startDate} onchange={(v) => applyFilters({ start_date: v })} />
        <FilterDate label="End Date" value={endDate} onchange={(v) => applyFilters({ end_date: v })} />
      {/key}
    {/snippet}
  </FilterBar>

  {#if !engagement || !growth || !moderation}
    <Card>
      <p class="text-base-content/60">No metrics available for this range.</p>
    </Card>
  {:else}
    <div class="space-y-6">
      <Card title="Engagement">
        <StatGrid>
          <StatTile label="Moments Created" value={engagement.moments_created.toLocaleString()} />
          <StatTile label="Hypes Given" value={engagement.hypes_given.toLocaleString()} />
          <StatTile label="Avg Hypes / Moment" value={engagement.avg_hypes_per_moment.toFixed(1)} />
          <StatTile label="Active Creators" value={engagement.active_creators.toLocaleString()} />
          <StatTile label="Active Hypers" value={engagement.active_hypers.toLocaleString()} />
        </StatGrid>
        <StatGrid>
          <StatTile label="Public" value={engagement.moments_by_visibility.public.toLocaleString()} />
          <StatTile label="Friends Only" value={engagement.moments_by_visibility.friends_only.toLocaleString()} />
          <StatTile label="Private" value={engagement.moments_by_visibility.private.toLocaleString()} />
        </StatGrid>
      </Card>

      <Card title="Growth">
        <StatGrid>
          <StatTile label="Friend Requests Sent" value={growth.friend_requests_sent.toLocaleString()} />
          <StatTile label="Accepted" value={growth.friend_requests_accepted.toLocaleString()} />
          <StatTile label="Acceptance Rate" value={formatRate(growth.acceptance_rate)} />
          <StatTile label="Total Friendships" value={growth.total_friendships.toLocaleString()} />
          <StatTile label="New Friendships" value={growth.new_friendships_in_range.toLocaleString()} />
        </StatGrid>
      </Card>

      <Card title="Moderation">
        <!-- Three rows of at most five: one 11-tile row runs off the card at desktop widths. -->
        <StatGrid>
          <StatTile label="Moment Reports" value={moderation.moment_reports.toLocaleString()} />
          <StatTile label="User Reports" value={moderation.user_reports.toLocaleString()} />
          <StatTile label="Currently Hidden Moments" value={moderation.currently_hidden_moments.toLocaleString()} />
          <StatTile label="Blocks" value={moderation.blocks_created.toLocaleString()} />
          <StatTile label="Mutes" value={moderation.mutes_created.toLocaleString()} />
        </StatGrid>
        <StatGrid>
          <StatTile
            label="Pending Moment Reviews"
            value={moderation.pending_moment_reviews.toLocaleString()}
            highlight={moderation.pending_moment_reviews > 0}
          />
          <StatTile
            label="Moment Reviews Over 24h"
            value={(moderation.overdue_moment_reviews ?? 0).toLocaleString()}
            highlight={moderation.overdue_moment_reviews > 0}
          />
          <StatTile label="Avg Time to Action (Moments)" value={formatHours(moderation.avg_moment_report_hours_to_action)} />
        </StatGrid>
        <StatGrid>
          <StatTile
            label="Pending User Reviews"
            value={moderation.pending_user_reviews.toLocaleString()}
            highlight={moderation.pending_user_reviews > 0}
          />
          <StatTile
            label="User Reviews Over 24h"
            value={(moderation.overdue_user_reviews ?? 0).toLocaleString()}
            highlight={moderation.overdue_user_reviews > 0}
          />
          <StatTile label="Avg Time to Action (Users)" value={formatHours(moderation.avg_user_report_hours_to_action)} />
        </StatGrid>

        {#if queue?.moment_reviews || queue?.user_reviews}
          <StatGrid>
            {#if queue?.moment_reviews}
              <div class="stat">
                <div class="stat-title">Oldest Pending Moment Review</div>
                <div class="stat-value text-2xl">
                  <AgeBadge timestamp={queue.moment_reviews.oldest_pending_at} staleAfterHours={24} criticalAfterHours={72} />
                  {#if !queue.moment_reviews.oldest_pending_at}—{/if}
                </div>
              </div>
            {/if}
            {#if queue?.user_reviews}
              <div class="stat">
                <div class="stat-title">Oldest Pending User Review</div>
                <div class="stat-value text-2xl">
                  <AgeBadge timestamp={queue.user_reviews.oldest_pending_at} staleAfterHours={24} criticalAfterHours={72} />
                  {#if !queue.user_reviews.oldest_pending_at}—{/if}
                </div>
              </div>
            {/if}
          </StatGrid>
        {/if}
      </Card>

      <Card title="Trends">
        <LineChart labels={timeSeries.labels} datasets={trendDatasets} height={320} />
      </Card>
    </div>
  {/if}
</Layout>
